import {Stack} from 'src/design/atoms/Stack';
import React, {useCallback, useEffect, useState} from 'react';
import type {IFormDataV2} from 'src/types/FormV2';
import {GeneralSection} from './sections/GeneralSection';
import {LabelsSection} from './sections/LabelsSection';
import {RiskMatrixSection} from './sections/RiskMatrixSection';

export interface RiskAssessmentContentTabProps {
  formData: IFormDataV2;
  onChange: (name: string, value: any) => void;
  mode: 'create' | 'edit' | 'view';
  validationFnRef?: React.MutableRefObject<(() => boolean) | undefined>;
  matrixValidationFnRef?: React.MutableRefObject<
    (() => boolean) | undefined
  >;
}

export const RiskAssessmentContentTab: React.FC<
  RiskAssessmentContentTabProps
> = ({formData, onChange, mode, validationFnRef, matrixValidationFnRef}) => {
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name || !String(formData.name).trim()) {
      newErrors.name = 'Name is required';
    }
    if (!formData.area) {
      newErrors.area = 'Risk Assessment Area is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData.name, formData.area]);

  useEffect(() => {
    if (validationFnRef) {
      validationFnRef.current = validateForm;
    }
  }, [validationFnRef, validateForm]);

  const handleChange = useCallback(
    (name: string, value: any) => {
      onChange(name, value);
      if (errors[name]) {
        setErrors(prev => {
          const next = {...prev};
          delete next[name];
          return next;
        });
      }
    },
    [onChange, errors]
  );

  return (
    <Stack gap="md" data-testid="risk-assessment-content-tab">
      <GeneralSection
        formData={formData}
        onChange={handleChange}
        mode={mode}
        errors={errors}
      />
      <RiskMatrixSection
        formData={formData}
        onChange={handleChange}
        mode={mode}
        validationFnRef={matrixValidationFnRef}
      />
      <LabelsSection
        formData={formData}
        onChange={handleChange}
        mode={mode}
        type="probability"
      />
      <LabelsSection
        formData={formData}
        onChange={handleChange}
        mode={mode}
        type="severity"
      />
    </Stack>
  );
};
